import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { setReturnTo } from "../lib/returnTo";

// Research articles: the spreadsheet view of the model (loaded only when opened).
const SheetViewer = lazy(() => import("../components/SheetViewer"));
import { useLocation, useNavigate } from "react-router-dom";
import AnalystBroNavbar from "../components/AnalystBroNavbar";
import AnalystBroFooter from "../components/AnalystBroFooter";

// Market data pages (/markets/DSE, /markets/DSE/stocks/<view>, /markets/bd/funds/...): the server
// renders the full page (Stock-Screener plan 300, api/market_pages). On first load we keep the HTML
// the server put in #root (captured in main.jsx before React mounts); on in-app navigation we fetch
// the same URL and take its #root content. One source of truth for crawlers and people.

const THEME = "ab-light";

function extract(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const root = doc.getElementById("root");
  return { body: root ? root.innerHTML : "", title: doc.title };
}

function clean(html) {
  // The server's placeholder nav bar is replaced by the real one.
  return (html || "").replace('<div class="abp-nav"></div>', "");
}

// Sorting and filtering for the server-rendered tables (no server round trip).
function cellValue(td) {
  const t = (td?.textContent || "").trim();
  if (!t || t === "not available") return null;
  const n = t.replace(/[৳,%+\s]/g, "").replace(/[−–]/g, "-");
  if (/^-?\d+(\.\d+)?$/.test(n)) return parseFloat(n);
  const d = Date.parse(t);
  if (/\d{4}/.test(t) && !Number.isNaN(d)) return d;
  return t.toLowerCase();
}

function enhanceTables(root) {
  root.querySelectorAll(".abm .wrap").forEach((wrap) => {
    if (wrap.dataset.enhanced) return;
    wrap.dataset.enhanced = "1";
    const table = wrap.querySelector("table");
    const tbody = table?.tBodies[0];
    if (!tbody) return;
    const rows = Array.from(tbody.rows);
    if (rows.length > 5) {
      const box = document.createElement("div");
      box.className = "tfilter";
      box.innerHTML = '<input type="search" placeholder="Filter rows (ticker, name, sector…)" aria-label="Filter rows" /><span></span>';
      wrap.parentNode.insertBefore(box, wrap);
      const input = box.querySelector("input");
      const count = box.querySelector("span");
      const update = () => {
        const q = input.value.trim().toLowerCase();
        let shown = 0;
        rows.forEach((r) => {
          const ok = !q || r.textContent.toLowerCase().includes(q);
          r.style.display = ok ? "" : "none";
          if (ok) shown += 1;
        });
        count.textContent = q ? `${shown} of ${rows.length}` : "";
      };
      input.addEventListener("input", update);
    }
    const ths = Array.from(table.tHead?.rows[0]?.cells || []);
    ths.forEach((th, i) => {
      th.classList.add("sortable");
      th.title = "Sort";
      th.addEventListener("click", () => {
        const dir = th.dataset.dir === "desc" ? "asc" : "desc";
        ths.forEach((h) => { delete h.dataset.dir; });
        th.dataset.dir = dir;
        const sorted = rows.slice().sort((a, b) => {
          const x = cellValue(a.cells[i]);
          const y = cellValue(b.cells[i]);
          if (x === null && y === null) return 0;
          if (x === null) return 1;          // blanks always last
          if (y === null) return -1;
          if (typeof x === "number" && typeof y === "number") return dir === "asc" ? x - y : y - x;
          return dir === "asc" ? String(x).localeCompare(String(y)) : String(y).localeCompare(String(x));
        });
        sorted.forEach((r) => tbody.appendChild(r));
      });
    });
  });
}

const TABLE_CSS = `.abm th.sortable{cursor:pointer;user-select:none}.abm th.sortable:hover{color:#1d4ed8}
.abm th[data-dir="desc"]::after{content:" ▼";font-size:.6rem}.abm th[data-dir="asc"]::after{content:" ▲";font-size:.6rem}
.abm .tfilter{display:flex;align-items:center;gap:.6rem;margin:.6rem 0 .25rem}
.abm .tfilter input{width:100%;max-width:22rem;font-size:.85rem;padding:.45rem .7rem;border:1px solid #cbd5e1;border-radius:8px;background:#fff;color:#0f172a;outline:none}
.abm .tfilter input:focus{border-color:#2563eb;box-shadow:0 0 0 3px rgba(37,99,235,.12)}
.abm .tfilter span{font-size:.75rem;color:#64748b;white-space:nowrap}`;

export default function MarketDataPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const url = location.pathname + location.search;
  const initial = window.__SSR_ROOT__ && window.__SSR_ROOT__.url === url ? window.__SSR_ROOT__.html : null;
  const [state, setState] = useState({ html: clean(initial), loading: !initial, error: false });
  const box = useRef(null);
  const [viewer, setViewer] = useState(false);
  useEffect(() => {
    if (box.current) enhanceTables(box.current);
  }, [state.html]);

  useEffect(() => {
    if (window.__SSR_ROOT__ && window.__SSR_ROOT__.url === url) {
      window.__SSR_ROOT__ = null;  // used once; later visits fetch
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: false }));
    fetch(url, { headers: { Accept: "text/html" } })
      .then((r) => r.text().then((t) => ({ ok: r.ok, t })))
      .then(({ ok, t }) => {
        if (cancelled) return;
        const { body, title } = extract(t);
        if (title) document.title = title;
        setState({ html: clean(body), loading: false, error: !ok });
      })
      .catch(() => !cancelled && setState({ html: "", loading: false, error: true }));
    window.scrollTo(0, 0);
    return () => {
      cancelled = true;
    };
  }, [url]);

  // Internal links inside the server HTML navigate in-app (CSV downloads stay normal links).
  const onClick = (e) => {
    if (e.target.closest && e.target.closest("[data-xlsx-view]")) {
      setViewer(true);
      return;
    }
    if (e.target.closest && e.target.closest("[data-xlsx-download]")) {
      e.preventDefault();
      downloadXlsx();
      return;
    }
    const a = e.target.closest && e.target.closest("a");
    if (!a || e.metaKey || e.ctrlKey || e.shiftKey || a.target === "_blank") return;
    const href = a.getAttribute("href") || "";
    if (!href.startsWith("/") || href.includes("format=csv")) return;
    e.preventDefault();
    navigate(href);
  };

  // Paid accounts: the article's Excel model (401 → sign in, 402 → pricing).
  const downloadXlsx = async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/api/research/xlsx/`, {
        params: { path: location.pathname, preview: new URLSearchParams(location.search).get("preview") || undefined },
        responseType: "blob",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `AnalystBro_${location.pathname.split("/").pop()}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      const st = err?.response?.status;
      if (st === 401) {
        setReturnTo(location.pathname);
        navigate("/login");
      } else navigate("/pricing");
    }
  };

  return (
    <div className={`${THEME} min-h-screen flex flex-col font-sans antialiased`}>
      {viewer && (
        <Suspense fallback={null}>
          <SheetViewer path={location.pathname} preview={new URLSearchParams(location.search).get("preview")}
            onClose={() => setViewer(false)} />
        </Suspense>
      )}
      <AnalystBroNavbar theme={THEME} />
      <style>{TABLE_CSS}</style>
      <div className="flex-1 w-full" onClick={onClick} ref={box}>
        {state.loading && !state.html ? (
          <p className="max-w-6xl mx-auto px-8 py-10 text-sm text-slate-500">Loading…</p>
        ) : state.error && !state.html ? (
          <p className="max-w-6xl mx-auto px-8 py-10 text-sm text-slate-500">This page could not be loaded.</p>
        ) : (
          <div dangerouslySetInnerHTML={{ __html: state.html }} />
        )}
      </div>
      <AnalystBroFooter theme={THEME} />
    </div>
  );
}
